/**
 * Super-admin runtime configuration (assistant on/off, maintenance banner).
 *
 * Resolution order for each setting (first match wins):
 *   1. Device override in localStorage — only used as an OFFLINE fallback when
 *      no settings service is configured (demo mode). A global save never writes
 *      it, so it can't shadow a global change.
 *   2. GLOBAL settings from the settings service (GET /settings) — the source of
 *      truth for ALL users. A super admin's PUT persists server-side, so the
 *      change reaches every browser on its next load. No file upload / redeploy.
 *   3. Published /config.json (legacy global fallback if no settings service).
 *   4. Build-time env var (import.meta.env.VITE_*).
 *   5. Safe default.
 *
 * loadServerSettings() (called at startup from main.jsx) fetches the global
 * settings once; getters read the cache synchronously afterwards. Saving updates
 * the cache and notifies subscribers so this admin's UI reflects it immediately;
 * other users pick it up on their next load.
 */
import { getConfig } from '../utils/runtimeConfig';
import { fetchAppSettings, saveAppSettings, settingsBackendEnabled } from '../api/settingsApi';

const LS_KEYS = {
  assistantEnabled: 'sendero_cfg_assistant_enabled',
  loginAssistantEnabled: 'sendero_cfg_login_assistant_enabled',
  maintenanceEnabled: 'sendero_cfg_maintenance_enabled',
  maintenanceMessage: 'sendero_cfg_maintenance_message',
};

const EVENT_NAME = 'sendero:config-change';

const readLS = (key) => {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) || '' : ''; }
  catch { return ''; }
};
const writeLS = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch { /* ignore */ }
};
const notify = () => { try { window.dispatchEvent(new CustomEvent(EVENT_NAME)); } catch { /* ignore */ } };

// Global settings cache (from the settings service). null until loaded.
let server = null;

export const appConfig = {
  /** Whether a global settings service is wired (vs. offline device mode). */
  hasSettingsBackend: settingsBackendEnabled,

  /** Fetch the global settings once at startup. Safe if the service is absent. */
  async loadServerSettings() {
    if (!settingsBackendEnabled()) { server = null; return null; }
    server = await fetchAppSettings();
    notify();
    return server;
  },

  /* -- AI assistant ---------------------------------------------------- */
  getAssistantEnabled() {
    const override = readLS(LS_KEYS.assistantEnabled);
    if (!settingsBackendEnabled()) { // offline device mode
      if (override === '1') return true;
      if (override === '0') return false;
    }
    if (server && typeof server.assistantEnabled === 'boolean') return server.assistantEnabled;
    const global = getConfig().assistantEnabled;
    if (typeof global === 'boolean') return global;
    const env = String(import.meta.env.VITE_ASSISTANT_ENABLED ?? '').toLowerCase();
    if (env === 'false' || env === '0') return false;
    if (env === 'true' || env === '1') return true;
    return true; // default: assistant on
  },

  /** Save globally (all users). Falls back to a device override with no backend. */
  async saveAssistantEnabled(value) {
    if (!settingsBackendEnabled()) { writeLS(LS_KEYS.assistantEnabled, value ? '1' : '0'); notify(); return; }
    server = await saveAppSettings({ assistantEnabled: !!value });
    notify();
  },

  /* -- pre-login (sign-in page) public help assistant ------------------ */
  // Separate switch from the in-app assistant. OFF by default — a super admin
  // opts in. The public assistant is a limited helper that answers general
  // questions only and can never reach PHI (tool-less backend endpoint).
  getLoginAssistantEnabled() {
    const override = readLS(LS_KEYS.loginAssistantEnabled);
    if (!settingsBackendEnabled()) { // offline device mode
      if (override === '1') return true;
      if (override === '0') return false;
    }
    if (server && typeof server.loginAssistantEnabled === 'boolean') return server.loginAssistantEnabled;
    const global = getConfig().loginAssistantEnabled;
    if (typeof global === 'boolean') return global;
    const env = String(import.meta.env.VITE_LOGIN_ASSISTANT_ENABLED ?? '').toLowerCase();
    return env === 'true' || env === '1'; // default: OFF
  },
  /** Save globally (all users). Falls back to a device override with no backend. */
  async saveLoginAssistantEnabled(value) {
    if (!settingsBackendEnabled()) { writeLS(LS_KEYS.loginAssistantEnabled, value ? '1' : '0'); notify(); return; }
    server = await saveAppSettings({ loginAssistantEnabled: !!value });
    notify();
  },

  /* -- maintenance banner --------------------------------------------- */
  getMaintenanceEnabled() {
    if (!settingsBackendEnabled()) {
      const override = readLS(LS_KEYS.maintenanceEnabled);
      if (override === '1') return true;
      if (override === '0') return false;
    }
    if (server && server.maintenance && typeof server.maintenance.enabled === 'boolean') return server.maintenance.enabled;
    const global = getConfig().maintenance;
    if (global && typeof global.enabled === 'boolean') return global.enabled;
    const env = String(import.meta.env.VITE_MAINTENANCE_ENABLED ?? '').toLowerCase();
    return env === 'true' || env === '1';
  },
  getMaintenanceMessage() {
    if (!settingsBackendEnabled()) {
      const override = readLS(LS_KEYS.maintenanceMessage);
      if (override) return override;
    }
    if (server && server.maintenance && typeof server.maintenance.message === 'string' && server.maintenance.message) return server.maintenance.message;
    const global = getConfig().maintenance;
    if (global && typeof global.message === 'string' && global.message) return global.message;
    return import.meta.env.VITE_MAINTENANCE_MESSAGE || '';
  },

  /** Save globally (all users). Falls back to a device override with no backend. */
  async saveMaintenance(enabled, message) {
    if (!settingsBackendEnabled()) {
      writeLS(LS_KEYS.maintenanceMessage, (message || '').trim());
      writeLS(LS_KEYS.maintenanceEnabled, enabled ? '1' : '0');
      notify();
      return;
    }
    server = await saveAppSettings({ maintenance: { enabled: !!enabled, message: (message || '').trim() } });
    notify();
  },

  /* -- inactivity auto-deactivation (PHI safeguard) -------------------- */
  // { enabled, days }: accounts with no login for `days` are auto-deactivated
  // by a scheduled backend job. Config lives server-side so it's global.
  getInactivity() {
    const s = (server && server.inactivity) || getConfig().inactivity || null;
    return { enabled: Boolean(s?.enabled), days: Number(s?.days) > 0 ? Number(s.days) : 60 };
  },
  /** Save globally (all users). Requires a settings service. */
  async saveInactivity(enabled, days) {
    const d = Math.min(3650, Math.max(1, Math.round(Number(days) || 60)));
    if (!settingsBackendEnabled()) throw new Error('A settings service is required to configure account inactivity.');
    server = await saveAppSettings({ inactivity: { enabled: !!enabled, days: d } });
    notify();
  },
  /**
   * Whether an account should be considered dormant per the current rule.
   * `lastLogin` is an ISO string/date; `now` overridable for tests.
   */
  isAccountDormant(lastLogin, now = Date.now()) {
    const cfg = this.getInactivity();
    if (!cfg.enabled || !lastLogin) return false;
    const t = new Date(lastLogin).getTime();
    if (!Number.isFinite(t)) return false;
    return (now - t) > cfg.days * 24 * 60 * 60 * 1000;
  },

  /* -- feedback / testimonials ---------------------------------------- */
  // { collect: boolean, publish: 'off' | 'dashboard' | 'help' }
  // collect  → show the footer "Rate your experience" link + widget
  // publish  → where approved/featured testimonials appear (never on login)
  getFeedbackConfig() {
    const s = (server && server.feedback) || getConfig().feedback || null;
    const publish = ['off', 'dashboard', 'help'].includes(s?.publish) ? s.publish : 'off';
    return { collect: s?.collect !== false, publish };
  },
  /** Save globally (all users). Falls back to an in-memory device override with no backend. */
  async saveFeedbackConfig({ collect, publish }) {
    const patch = { feedback: {} };
    if (typeof collect === 'boolean') patch.feedback.collect = collect;
    if (publish != null) patch.feedback.publish = publish;
    if (!settingsBackendEnabled()) {
      // device-only fallback (demo): keep it in the local server cache for this session.
      server = { ...(server || {}), feedback: { ...this.getFeedbackConfig(), ...patch.feedback } };
      notify();
      return;
    }
    server = await saveAppSettings(patch);
    notify();
  },

  /** Subscribe to config changes (incl. cross-tab). Returns an unsubscribe fn. */
  subscribe(listener) {
    const handler = () => listener();
    window.addEventListener(EVENT_NAME, handler);
    const storageHandler = (e) => { if (e.key && Object.values(LS_KEYS).includes(e.key)) listener(); };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener('storage', storageHandler);
    };
  },
};

export default appConfig;
